/**
 * Animation utilities for mobile-responsive EcoFashion website
 * Handles scroll-triggered animations and mobile optimizations
 */

// Intersection Observer for scroll animations
let animationObserver = null;

// Initialize scroll animations
export const initScrollAnimations = () => {
  // Check if IntersectionObserver is supported
  if (!window.IntersectionObserver) {
    // Fallback: just show all elements
    showAllElements();
    return;
  }

  // Create observer with mobile-optimized settings
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -100px 0px', // Trigger animation earlier on mobile
    threshold: 0.1
  };

  // Adjust for mobile devices
  if (window.innerWidth <= 768) {
    observerOptions.rootMargin = '0px 0px -50px 0px';
    observerOptions.threshold = 0.05;
  }

  animationObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        // Unobserve element after animation for performance
        animationObserver.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe all animation elements
  observeElements();
};

// Observe elements for animations
const observeElements = () => {
  const animationSelectors = [
    '.fade-in',
    '.slide-up',
    '.slide-left',
    '.slide-right',
    '.scale-in',
    '.contact-section'
  ];

  animationSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      if (animationObserver) {
        animationObserver.observe(element);
      }
    });
  });
};

// Fallback function for unsupported browsers
const showAllElements = () => {
  const elements = document.querySelectorAll('.fade-in, .slide-up, .slide-left, .slide-right, .scale-in, .contact-section');
  elements.forEach(element => {
    element.classList.add('visible');
    element.style.opacity = '1';
    element.style.transform = 'none';
  });
};

// Smooth scroll to element
export const smoothScrollTo = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementPosition - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

// Mobile menu animations
export const animateMenuToggle = (isOpen) => {
  const body = document.body;
  
  if (isOpen) {
    body.classList.add('mobile-nav-open');
    // Add entrance animation to menu items
    const menuItems = document.querySelectorAll('.mobile-menu-item');
    menuItems.forEach((item, index) => {
      item.style.animationDelay = `${index * 0.1}s`;
      item.classList.add('menu-item-enter');
    });
  } else {
    body.classList.remove('mobile-nav-open');
    // Remove animation classes
    const menuItems = document.querySelectorAll('.mobile-menu-item');
    menuItems.forEach(item => {
      item.classList.remove('menu-item-enter');
      item.style.animationDelay = '';
    });
  }
};

// Optimize animations based on device performance
export const optimizeAnimationsForDevice = () => {
  const isLowEndDevice = () => {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }
    
    // Check for low-end device indicators
    const userAgent = navigator.userAgent.toLowerCase();
    const isOldAndroid = /android [1-4]/.test(userAgent);
    const isOldIOS = /os [1-9]_/.test(userAgent);
    const isSlowConnection = navigator.connection && 
      (navigator.connection.effectiveType === 'slow-2g' || 
       navigator.connection.effectiveType === '2g');
    
    return isOldAndroid || isOldIOS || isSlowConnection;
  };

  if (isLowEndDevice()) {
    // Disable complex animations on low-end devices
    document.documentElement.style.setProperty('--animation-duration', '0.1s');
    document.documentElement.classList.add('reduced-animations');
  }
};

// Parallax effect for desktop only
export const initParallaxEffects = () => {
  // Only enable on larger screens and non-touch devices
  if (window.innerWidth > 1024 && !('ontouchstart' in window)) {
    const parallaxElements = document.querySelectorAll('.parallax');
    
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;
      
      parallaxElements.forEach(element => {
        element.style.transform = `translateY(${rate}px)`;
      });
    };

    // Throttle scroll events for performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll);
  }
};

// Initialize all animations on DOM load
export const initAllAnimations = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeAnimationsForDevice();
      initScrollAnimations();
      initParallaxEffects();
    });
  } else {
    optimizeAnimationsForDevice();
    initScrollAnimations();
    initParallaxEffects();
  }

  // Reinitialize on route changes (for React Router)
  const reinitializeOnRouteChange = () => {
    setTimeout(() => {
      observeElements();
    }, 100);
  };

  // Listen for route changes
  window.addEventListener('popstate', reinitializeOnRouteChange);
  
  // For React Router, also listen for pushstate
  const originalPushState = window.history.pushState;
  window.history.pushState = function() {
    originalPushState.apply(window.history, arguments);
    reinitializeOnRouteChange();
  };
};

// Cleanup function
export const cleanupAnimations = () => {
  if (animationObserver) {
    animationObserver.disconnect();
    animationObserver = null;
  }
};

// Performance monitoring
export const monitorAnimationPerformance = () => {
  if ('performance' in window && 'observe' in window.PerformanceObserver.prototype) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 16.67) { // 60fps threshold
          console.warn(`Slow animation detected: ${entry.name} took ${entry.duration}ms`);
        }
      }
    });
    
    observer.observe({ entryTypes: ['measure'] });
  }
};

// Export default initialization
const animationUtils = {
  init: initAllAnimations,
  cleanup: cleanupAnimations,
  smoothScroll: smoothScrollTo,
  menuToggle: animateMenuToggle,
  monitor: monitorAnimationPerformance
};

export default animationUtils;